import Logger from 'observability/Logger';
import { PUBSUB_CMS_FRAMES_INITIALIZED, PUBSUB_CONTEXT } from '../constants';

/**
 * We heard you liked iframes, so we put an iframe in your iframe
 * so you can load while you load:
 *
 * When we reach into the CMS iframe's code from the Workspace react-land,
 * to open sidebars, open confirmations, open the share card, etc - we wait
 * for the CMS iframe to load.
 *
 * But the CMS edit view iframe has it's _own_ iframe. And those functions often
 * don't work unless both are fully loaded.
 *
 * Different browsers fire the parent iframe's load event at different times,
 * sometimes before, sometimes after the "grandchild" iframe has loaded.
 *
 */

const LOG_LABEL = 'waitForChildIframe';
const LOG = new Logger(LOG_LABEL);

/**
 * @param {Window} iframeWindow
 * @return {Promise}
 */
const waitForPubSub = (iframeWindow) =>
    new Promise((resolve, reject) => {
        try {
            const { workspaceAndPageInitialized, PubSub } = iframeWindow.splash;

            if (workspaceAndPageInitialized) {
                LOG.info('Running callback right away');
                resolve();
            } else {
                LOG.info('Waiting for PubSub');
                PubSub.subscribe(PUBSUB_CMS_FRAMES_INITIALIZED, {
                    context: PUBSUB_CONTEXT,
                    eventHandler: () => {
                        LOG.info('PubSub event published, running callback');
                        resolve();
                    },
                });
            }
        } catch (error) {
            LOG.error('Error on waitForPubSub.', error);
            reject(error);
        }
    });

/**
 * @param {Object} iframeWindow
 * @param {function} callback
 */
export default function waitForChildIframe(iframeWindow, callback) {
    const childIframe = iframeWindow.document.querySelector('iframe#cmsPageIframe');

    if (!childIframe) {
        throw new Error('Inner page iframe not found');
    }

    if (!childIframe.contentDocument && !childIframe.contentWindow.document) {
        throw new Error('Inner page iframe contents not found');
    }

    const childIframeDoc = childIframe.contentDocument || childIframe.contentWindow.document;

    // If inner frame is still not interactive or complete, we must wait for its JS to be executed
    if (childIframeDoc && childIframeDoc.readyState === 'loading') {
        LOG.info('Child document not interactive, adding listener');
        childIframeDoc.addEventListener('DOMContentLoaded', () => {
            waitForPubSub(iframeWindow)
                .then(() => callback())
                .catch((error) => {
                    LOG.error('Error on addEventListener.', error);
                });
        });
    } else {
        waitForPubSub(iframeWindow)
            .then(() => callback())
            .catch((error) => {
                LOG.error('Error on waitForPubSub.', error);
            });
    }
}
